import React, { useEffect, useState } from "react";
import {
  Box, Container, Typography, Stack, IconButton, 
  Card, CardContent, alpha, useTheme, Avatar, Skeleton, Paper
} from "@mui/material";
import {
  ArrowBackIosNew as PrevIcon,
  ArrowForwardIos as NextIcon,
  AccessTime,
  EventAvailable,
  SentimentDissatisfied
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAllBookings } from "../redux/slices/booking/thunk";
import { useTranslation } from "react-i18next";
import useBaseImageUrl from "../hooks/useBaseImageUrl";



export default function MyBookingsPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const baseUrl = useBaseImageUrl();
  const { i18n, t } = useTranslation();
  const isRtl = i18n.language === "ar";

  const { bookings, loading } = useSelector((state) => state.booking);
  const [targetDate, setTargetDate] = useState(new Date());

  // دالة لتحويل التاريخ لصيغة DD-MM-YYYY
  const formatDateForQuery = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };

  useEffect(() => {
    const dateQuery = formatDateForQuery(targetDate);
    // إرسال التاريخ في الكويري كما طلبت
    const query = `limit=1000&page=1&date=${dateQuery}`;
    dispatch(getAllBookings({ query }));
  }, [dispatch, targetDate]);

  const handleDateChange = (offset) => {
    const newDate = new Date(targetDate);
    newDate.setDate(targetDate.getDate() + offset);
    setTargetDate(newDate);
  };


  const getStatusStyles = (status) => {
  switch (status) {
    case 'pending':
      return { label: t("bookings.status.pending"), color: 'warning', bgColor: alpha(theme.palette.warning.main, 0.1) };
    case 'cancelled':
      return { label: t("bookings.status.cancelled"), color: 'error', bgColor: alpha(theme.palette.error.main, 0.1) };
    case 'completed':
      return { label: t("bookings.status.completed"), color: 'success', bgColor: alpha(theme.palette.success.main, 0.1) };
    default:
      return { label: status, color: 'default', bgColor: theme.palette.action.hover };
  }
};

  const calculateDetails = (item) => {
    const start = new Date(item.reservedStart);
    const end = new Date(item.reservedEnd);
    const duration = Math.abs(end - start) / 36e5;
    
    // حساب السعر يدوياً إذا كان 0 (بناءً على الـ JSON اللي بعته)
    let price = item.finalPrice || 0;
    if (price === 0) {
      const rate = item.currentType === "single" ? item.room?.price_single_hour : item.room?.price_multi_hour;
      price = duration * (rate || 0);
    }

    return {
      price: price.toLocaleString(),
      duration: duration.toFixed(0),
      timeRange: `${start.toLocaleTimeString(isRtl ? 'ar-EG' : 'en-US', { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString(isRtl ? 'ar-EG' : 'en-US', { hour: '2-digit', minute: '2-digit' })}`
    };
  };

  return (
    <Box sx={{ minHeight: "100vh", pb: 2 }}>
      {/* Date Swiper Header */}
      <Paper elevation={0} sx={{ 
        py: 4, mb: 3, borderBottom: `1px solid ${theme.palette.divider}`,
        borderRadius: 0, bgcolor: 'background.paper' 
      }}>
        <Container maxWidth="md">
          <Typography variant="h5" fontWeight={1000} sx={{ mb: 3, textAlign: 'center', letterSpacing: -0.5 }}>
            {t("bookings.title")}
          </Typography>

          <Stack direction="row" alignItems="center" justifyContent="center" spacing={3}>
            <IconButton 
              onClick={() => handleDateChange(-1)} 
              sx={{ bgcolor: 'action.hover', '&:hover': { bgcolor: 'action.selected' } }}
            >
              <PrevIcon sx={{ fontSize: 18, transform: isRtl ? "rotate(180deg)" : "none" }} />
            </IconButton>
            
            <Box 
              onClick={() => setTargetDate(new Date())}
              sx={{ 
                textAlign: 'center', cursor: 'pointer', minWidth: 150,
                transition: '0.2s', '&:active': { transform: 'scale(0.95)' }
              }}
            >
              <Typography variant="h6" fontWeight={900} color="primary.main">
                {targetDate.toDateString() === new Date().toDateString() 
                  ? t("bookings.today") 
                  : targetDate.toLocaleDateString(isRtl ? 'ar-EG' : 'en-US', { month: 'long', day: 'numeric' })}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                {targetDate.getFullYear()}
              </Typography>
            </Box>

            <IconButton 
              onClick={() => handleDateChange(1)}
              sx={{ bgcolor: 'action.hover', '&:hover': { bgcolor: 'action.selected' } }}
            >
              <NextIcon sx={{ fontSize: 18, transform: isRtl ? "rotate(180deg)" : "none" }} />
            </IconButton>
          </Stack>
        </Container>
      </Paper>

      {/* Content */}
      <Container maxWidth="md">
        {loading ? (
          <Stack spacing={2}>
            {[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={110} sx={{ borderRadius: 2 }} />)}
          </Stack>
        ) : bookings?.data?.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 8, opacity: 0.6 }}>
            <SentimentDissatisfied sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6" fontWeight={700}>{t("bookings.no_bookings")}</Typography>
          </Box>
        ) : (
          <Stack spacing={2.5}>
            {bookings?.data?.map((item) => {
              const details = calculateDetails(item);
              return (
                <Card 
                  key={item._id} 
                  onClick={() => navigate(`/bookingdetails/${item._id}`)}
                  sx={{ 
                    borderRadius: 2, cursor: 'pointer', border: `1px solid ${theme.palette.divider}`,
                    boxShadow: 'none', transition: '0.3s',
                    '&:hover': { borderColor: 'primary.main', boxShadow: `0 10px 30px ${alpha(theme.palette.primary.main, 0.08)}` }
                  }}
                >
                  <CardContent sx={{ p: '20px !important' }}>
                    <Stack direction="row" justifyContent="flex-end" sx={{ mb: -1 }}>
    {(() => {
      const statusStyle = getStatusStyles(item.status);
      return (
        <Typography 
          variant="caption" 
          fontWeight={900} 
          sx={{ 
            color: `${statusStyle.color}.main`, 
            bgcolor: statusStyle.bgColor,
            px: 1.5, py: 0.5, borderRadius: '6px',
            textTransform: 'uppercase', fontSize: '10px'
          }}
        >
          {statusStyle.label}
        </Typography>
      );
    })()}
  </Stack>

                    <Stack direction="row" spacing={2.5} alignItems="center">
                      <Avatar 
                        src={`${baseUrl}${item.room?.image}`} 
                        variant="rounded" 
                        sx={{ width: 80, height: 80, borderRadius: 2, boxShadow: theme.shadows[1] }} 
                      >
                        <EventAvailable />
                      </Avatar>
                      
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight={900} sx={{ lineHeight: 1.2, mb: 1 }}>
                          {isRtl ? item.room?.title_ar : item.room?.title_en}
                        </Typography>
                        
                        <Stack direction="row" flexWrap="wrap" gap={1.5}>
                          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'text.secondary' }}>
                            <AccessTime sx={{ fontSize: 16 }} />
                            <Typography variant="caption" fontWeight={800}>{details.timeRange}</Typography>
                          </Stack>
                          <Typography variant="caption" fontWeight={800} sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', px: 1, borderRadius: 1 }}>
                            {item.currentType === "single" ? t("bookings.single") : t("bookings.multi")}
                          </Typography>
                        </Stack>
                      </Box>

                      <Box sx={{ textAlign: isRtl ? 'left' : 'right' }}>
                        <Typography variant="h5" fontWeight={1000} color="primary.main">
                          {details.price}
                          <Typography component="span" variant="caption" fontWeight={800} sx={{ ml: 0.5 }}>
                            EGP
                          </Typography>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontWeight={700}>
                          {details.duration} {t("bookings.hours_short")}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </Container>
    </Box>
  );
}